import { AppStateService, HierarchyMenuItem } from '@siza-staff-care/shared/app-state';
import { startCase } from 'lodash-es';
import { menuStructure } from './menu-structure';

type FlattenedItem = {
	displayPath: string;
	paths: string[];
	key: string;
	item: HierarchyMenuItem;
};

let initialised = false;
let menuItemsByKey: Record<string, FlattenedItem>;

let flattenedItems: FlattenedItem[];

/**
 * Translates a report security item (e.g. the old Jade Web Forms form class name) into a menu path.
 * e.g. BankPaidReport -> Bank > Reports > Bank Paid Report
 * @param jadeReportClassFormName
 * @param appState
 * @params shortenPath - If true, the path will be shortened by removing the Reporting >, or Bank > Reports > prefix.
 * @returns
 */
export const translateJadeReportFormClassIntoMenuPath = (
	jadeReportClassFormName: string,
	appState: AppStateService,
	shortenPath = false,
): string => {
	initialiseItemsByKey(appState);

	if (!jadeReportClassFormName) {
		return '';
	}

	const matches = /^(.*?)(Report)?$/.exec(jadeReportClassFormName);
	const lookup = matches?.[1];
	if (!lookup) {
		throw new Error('Could not determine report class');
	}

	const item = menuItemsByKey[lookup];
	if (item) {
		let translated = item.displayPath;
		if (shortenPath) {
			const removePrefixes = ['Reporting >', 'Bank > Reports >'];
			for (const prefix of removePrefixes) {
				if (translated.startsWith(prefix)) {
					translated = translated.substring(prefix.length + 1);
					break;
				}
			}
		}

		return translated;
	}

	//console.log('Could not find report class', reportClass);

	// Fall back to just returning the report class.
	return jadeReportClassFormName;
};

const initialiseItemsByKey = (appState: AppStateService) => {
	if (initialised) {
		return;
	}

	const settings = appState.settings;
	flattenedItems = buildFlatItems(
		menuStructure(
			settings.allocationManagementScreenName ?? '',
			settings.allocationManagementName ?? '',
			settings.sexAsGender,
		),
	);
	menuItemsByKey = flattenedItems.reduce((dict, item) => {
		dict[item.key] = item;
		return dict;
	}, {});

	initialised = true;
};

const buildFlatItems = (items: HierarchyMenuItem[]): FlattenedItem[] => {
	if (!items) {
		return [];
	}

	const specialStartCase = (s: string) => {
		// Special case to handle the Leaves/Shifts menu item.
		// Without the special case, the / gets removed, which looks odd.
		if (s.includes('/')) {
			return s
				.split('/')
				.map((w) => startCase(w))
				.join('/');
		}
		return startCase(s);
	};

	const flatten = (item: HierarchyMenuItem, paths: string[], result: FlattenedItem[]) => {
		const displayPath = [...paths, item.name].join(' > ');

		// Construct the flattened item.
		const newItem: FlattenedItem = {
			displayPath: displayPath,
			paths: [...paths, specialStartCase(item.name)],
			key: item.key,
			item,
		};
		result.push(newItem);

		if (!item.children) {
			return;
		}

		// Recurse into the children.
		item.children.forEach((child) => flatten(child, newItem.paths, result));
	};

	const result = [];
	items.forEach((i) => {
		flatten(i, [], result);
	});

	return result;
};
